import { ClipboardList } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export default function RoundSummary({
  notes,
  teams,
  isPro,
  isRoundEnded,
  roundVersion,
}) {
  const [reviewItems, setReviewItems] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const canUseSummary = isPro && isRoundEnded;

  useEffect(() => {
    setReviewItems(null);
  }, [roundVersion]);

  const openRoundReview = async () => {
    const hasNotes = Object.values(notes).some((stageNotes) =>
      Object.values(stageNotes).some((note) => note.trim()),
    );
    if (!hasNotes) {
      toast.error("Add round notes before opening the review.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/round-summary`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            pro_team: teams.pro,
            con_team: teams.con,
            notes,
          }),
        },
      );
      if (response.status === 402) {
        toast.info("Round Review is available with Pro.");
        return;
      }
      if (response.status === 409) {
        toast.info("End the round before opening the review.");
        return;
      }
      if (!response.ok) throw new Error("Round Review could not be opened.");
      const review = await response.json();
      setReviewItems(review.items);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      className="round-summary section-reveal delay-two"
      data-testid="round-summary-section"
    >
      <p className="pro-only-section-label" data-testid="round-summary-pro-label">
        For Pro users
      </p>
      <div className="section-heading">
        <div>
          <p className="eyebrow">Post-round record</p>
          <h2>Round Review</h2>
        </div>
        {canUseSummary && (
          <button
            className="summary-action"
            onClick={openRoundReview}
            disabled={isLoading}
            data-testid="open-round-review-button"
          >
            <ClipboardList size={17} />
            {isLoading ? "Opening review" : "Open Round Review"}
          </button>
        )}
      </div>
      {canUseSummary ? (
        <p className="note-review-disclosure" data-testid="round-review-disclosure">
          Displays only your entered notes, organized by speech stage and side.
        </p>
      ) : isRoundEnded ? (
        <p className="post-round-locked" data-testid="round-summary-pro-required-message">
          Round Review is available with Pro.
        </p>
      ) : (
        <p className="post-round-locked" data-testid="round-summary-locked-message">
          Available after you end the round.
        </p>
      )}
      {reviewItems && (
        <div className="note-review-output" data-testid="round-review-output">
          {reviewItems.map((item, index) => (
            <article
              key={`${item.stage}-${item.side}-${index}`}
              className="note-review-item"
            >
              <span>{item.stage} · {item.side}</span>
              <p>{item.text}</p>
            </article>
          ))}
          {!reviewItems.length && (
            <p className="note-review-empty" data-testid="round-review-empty">
              No entered notes to display.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
